import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, createInviteLink, listInviteLinks, revokeInviteLink, type InviteLink } from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import {
  BUTTON_GHOST_DANGER,
  BUTTON_PRIMARY,
  CARD,
  FIELD,
  FIELD_LABEL,
  SECTION_SUBTITLE,
  SECTION_TITLE,
} from '../controlStyles';
import { ORG_ROLES, assignableRoles, type OrgRole } from './memberDirectory';

const DAY_SECONDS = 24 * 60 * 60;
export const INVITE_LINK_EXPIRY_OPTIONS = [
  { label: '1 day', seconds: DAY_SECONDS },
  { label: '7 days', seconds: 7 * DAY_SECONDS },
  { label: '30 days', seconds: 30 * DAY_SECONDS },
] as const;

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function usage(link: InviteLink): string {
  if (link.maxUses !== null) return `${link.useCount} of ${link.maxUses} uses`;
  return link.useCount === 1 ? 'Used once' : `Used ${link.useCount} times`;
}

function failureText(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError && cause.status === 429) return 'Too many invite links were created recently. Try again in an hour.';
  if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) return cause.message;
  return fallback;
}

/**
 * Shareable invite links for Roles & members: mint one with a role and an
 * expiry, copy it, and revoke the ones still active. The full link exists
 * only in the create response (the server keeps a hash), so the list shows
 * each link's terms, not its URL.
 */
export function InviteLinksPanel({
  getTeamJwt,
  orgId,
  callerRole,
  disabled = false,
}: {
  getTeamJwt: () => Promise<TeamJwt>;
  orgId: string;
  callerRole: string;
  disabled?: boolean;
}) {
  const roles = useMemo(() => assignableRoles(callerRole), [callerRole]);
  const [links, setLinks] = useState<InviteLink[] | null>(null);
  const [role, setRole] = useState<OrgRole>('member');
  const [expiresInSeconds, setExpiresInSeconds] = useState<number>(7 * DAY_SECONDS);
  const [busy, setBusy] = useState<string | null>(null);
  const [created, setCreated] = useState<{ url: string; copied: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    const jwt = await getTeamJwt();
    const next = await listInviteLinks(jwt, orgId, signal);
    if (!signal?.aborted) setLinks(next);
  }, [getTeamJwt, orgId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      console.error('[invite-links] list failed', cause);
      setError(failureText(cause, 'Invite links could not be loaded.'));
    });
    return () => controller.abort();
  }, [load]);

  if (roles.length === 0) return null;

  const create = async () => {
    setBusy('create');
    setError(null);
    setCreated(null);
    try {
      const minted = await createInviteLink(await getTeamJwt(), orgId, { role, expiresInSeconds });
      let copied = false;
      try {
        await navigator.clipboard.writeText(minted.url);
        copied = true;
      } catch {
        // No clipboard permission: the link is shown below to copy by hand.
      }
      setCreated({ url: minted.url, copied });
      await load();
    } catch (cause) {
      console.error('[invite-links] create failed', cause);
      setError(failureText(cause, 'The invite link was not created. Try again in a moment.'));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (link: InviteLink) => {
    setBusy(`revoke:${link.id}`);
    setError(null);
    try {
      await revokeInviteLink(await getTeamJwt(), orgId, link.id);
      setLinks((current) => current?.filter((candidate) => candidate.id !== link.id) ?? null);
    } catch (cause) {
      console.error('[invite-links] revoke failed', cause);
      setError(failureText(cause, 'The invite link was not revoked. Try again in a moment.'));
    } finally {
      setBusy(null);
    }
  };

  const locked = disabled || busy !== null;

  return (
    <section className={`invite-links-card ${CARD}`} aria-labelledby="invite-links-heading">
      <header>
        <h3 id="invite-links-heading" className={SECTION_TITLE}>Invite links</h3>
        <p className={SECTION_SUBTITLE}>
          Anyone with a link can join with its role until it expires or is revoked. The link is shown once, when you create it.
        </p>
      </header>

      <div className="invite-link-create mt-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className={FIELD_LABEL}>Role</span>
          <select className={`${FIELD} capitalize`} value={role} onChange={(event) => setRole(event.target.value as OrgRole)} disabled={locked}>
            {ORG_ROLES.filter((candidate) => roles.includes(candidate)).map((candidate) => (
              <option key={candidate} value={candidate}>{candidate}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={FIELD_LABEL}>Expires after</span>
          <select className={FIELD} value={expiresInSeconds} onChange={(event) => setExpiresInSeconds(Number(event.target.value))} disabled={locked}>
            {INVITE_LINK_EXPIRY_OPTIONS.map((option) => (
              <option key={option.seconds} value={option.seconds}>{option.label}</option>
            ))}
          </select>
        </label>
        <button className={BUTTON_PRIMARY} type="button" disabled={locked} onClick={() => void create()}>
          {busy === 'create' ? 'Creating…' : 'Copy invite link'}
        </button>
      </div>

      {created && (
        <div className="invite-link-created mt-3 grid gap-1.5">
          <span className="text-[0.76rem] text-nim-muted" role="status">
            {created.copied ? 'Copied to the clipboard.' : 'Copy this link; it will not be shown again.'}
          </span>
          <input
            className={`${FIELD} select-text font-mono text-[0.78rem]`}
            value={created.url}
            readOnly
            aria-label="New invite link"
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      )}

      {error && <p className="m-0 mt-3 text-[0.76rem] leading-relaxed text-[var(--nim-error)]" role="alert">{error}</p>}

      <div className="invite-link-list mt-4 border-t border-nim">
        {links === null ? (
          <p className="m-0 py-4 text-center text-[0.82rem] text-nim-muted">Loading invite links…</p>
        ) : links.length === 0 ? (
          <p className="m-0 py-4 text-center text-[0.82rem] text-nim-muted">No active invite links.</p>
        ) : links.map((link) => (
          <div className="invite-link-row grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-nim py-2.5" key={link.id}>
            <div className="min-w-0">
              <strong className="block truncate text-[0.86rem] font-semibold capitalize text-nim">{link.role}</strong>
              <span className="block truncate text-[0.72rem] text-nim-muted">
                Expires {formatDate(link.expiresAt)} · {usage(link)} · created by {link.createdByEmail}
              </span>
            </div>
            <button
              className={`invite-link-revoke ${BUTTON_GHOST_DANGER}`}
              type="button"
              disabled={locked}
              aria-label={`Revoke ${link.role} invite link expiring ${formatDate(link.expiresAt)}`}
              onClick={() => void revoke(link)}
            >
              {busy === `revoke:${link.id}` ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
